import { useEffect, useMemo, useState } from 'react';
import { useBodyScrollLock } from '../hooks';
import { useLanguage } from '../i18n';

type Mode = 'text' | 'diff';

export default function CodeViewerModal({
  isOpen,
  onClose,
  title,
  subtitle,
  content,
  mode = 'text',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | null;
  content: string;
  mode?: Mode;
}) {
  const { lang } = useLanguage();
  const isKo = lang === 'ko';

  useBodyScrollLock(isOpen);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
  }, [isOpen]);

  const lines = useMemo(() => {
    if (!isOpen) return [];
    return (content || '').split('\n');
  }, [content, isOpen]);

  if (!isOpen) return null;

  const copyToClipboard = async () => {
    const text = content || '';
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / restricted contexts
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      // no-op: clipboard may be blocked by browser permissions
      setCopied(false);
    }
  };

  const renderLine = (line: string, idx: number) => {
    if (mode !== 'diff') {
      return (
        <div key={idx} className="whitespace-pre">
          {line}
        </div>
      );
    }

    // Diff color rules (unified diff):
    // - additions: lines starting with '+' (excluding '+++')
    // - deletions: lines starting with '-' (excluding '---')
    // - hunk: lines starting with '@@'
    // - headers: diff/index/---/+++ etc.
    const isHeader = line.startsWith('diff ') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++');
    const isHunk = line.startsWith('@@');
    const isAdd = !isHeader && line.startsWith('+');
    const isDel = !isHeader && line.startsWith('-');

    const cls = isHeader
      ? 'text-terminal-muted bg-terminal-dim/10'
      : isHunk
        ? 'text-sky-200 bg-sky-500/10'
        : isAdd
          ? 'text-emerald-200 bg-emerald-500/10'
          : isDel
            ? 'text-rose-200 bg-rose-500/10'
            : 'text-terminal-text';

    return (
      <div key={idx} className={`whitespace-pre px-2 ${cls}`}>
        {line}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-terminal-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-terminal-text truncate">{title}</div>
            {subtitle ? (
              <div className="text-xs text-terminal-muted mt-1 truncate">{subtitle}</div>
            ) : (
              <div className="text-xs text-terminal-muted mt-1 truncate">
                {isKo ? 'ESC 또는 바깥 영역 클릭으로 닫을 수 있어요.' : 'Press ESC or click outside to close.'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyToClipboard}
              className="px-3 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors whitespace-nowrap"
            >
              {copied ? (isKo ? '복사됨' : 'Copied') : (isKo ? '복사' : 'Copy')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors whitespace-nowrap"
            >
              {isKo ? '닫기' : 'Close'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <div className="text-xs font-mono border border-terminal-border rounded-lg overflow-hidden bg-black/35">
            {lines.length ? (
              <div className="py-2">{lines.map(renderLine)}</div>
            ) : (
              <div className="p-4 text-terminal-muted">{isKo ? '표시할 내용이 없습니다.' : 'No content.'}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


